<template>
  <div class="container pl-0 ml-0">
    <div class="row justify-content-start" v-if="initialized">
      <!-- category -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Category</h6>
        <CategoryFilter @update="categoryUpdate" :category="filteredCategory" />
      </div>

      <!-- sentiment -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Sentiment</h6>
        <SentimentFilter @update="sentimentUpdate" :sentiment="filteredSentiment" />
      </div>

      <!-- date -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Date Range</h6>
        <DatePicker
          @update="dateUpdate"
          :minDate="selectOldestCompanyStartDate([activeCompany._id])"
          :dateRange="filteredDateRange"
        />
      </div>

      <!-- company -->
      <div class="dropdown col-auto" v-if="activeCompany._id === 'ALL_COMPANIES'">
        <h6 class="header-pretitle mt-3">Companies</h6>
        <CompanyFilter
          @update="companyUpdate"
          :wideMode="true"
          :companies="selectInsightsCompanies"
          :activeCompany="activeCompany"
          :preselectedCompanies="this.filteredCompanies"
        />
      </div>

      <!-- location -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Locations</h6>
        <GroupingFilter
          :nonGroupMembers="locations"
          nonGroupMemberLabel="Locations"
          memberType="location"
          @update="groupingUpdate"
          :init="{
            nonGroupMemberIds: this.filteredLocations,
            groupIds: this.filteredGroups
          }"
          :key="locationRefreshKey"
          :companies="filteredCompanies"
        />
      </div>

      <!-- source -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Sources</h6>
        <SourceFilter
          @updateSources="sourceUpdate"
          :init="filteredSources"
          :multiCompany="true"
          :forInsights="true"
        />
      </div>

      <!-- keywords -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Keyword Search</h6>
        <TagFilter
          placeholder="Use a comma to search multiple keywords"
          @apply="keywordUpdate"
          :init="filteredKeywords"
        />
      </div>

      <!-- Add Filter Button -->
      <div class="col-auto dropdown">
        <h6 class="header-pretitle mt-3">&nbsp;</h6>
        <button class="btn btn-primary" @click="formatAndApplyFilters">Filter</button>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from '@/components/Modules/Filters/DatePicker'
import CategoryFilter from '../Filters/CategoryFilter'
import SentimentFilter from '../Filters/SentimentFilter'
import SourceFilter from '@/components/Modules/Filters/SourceFilter'
import GroupingFilter from '@/components/Modules/Filters/GroupingFilterV2'
import CompanyFilter from '@/components/Modules/Filters/CompanyFilter'
import TagFilter from '@/components/Modules/Filters/TagFilterV2'
import { createNamespacedHelpers } from 'vuex'

const InsightsDetailsModule = createNamespacedHelpers('insightDetailsv2')
const CompanyModule = createNamespacedHelpers('company')
const LocationModule = createNamespacedHelpers('location')

export default {
  name: 'InsightsDetailsFilters',
  components: {
    GroupingFilter,
    DatePicker,
    CategoryFilter,
    SentimentFilter,
    SourceFilter,
    CompanyFilter,
    TagFilter
  },
  data: () => ({
    initialized: false,
    initialLoadComplete: false,
    filteredDateRange: null,
    filteredGroups: null,
    filteredGroupMembers: null,
    filteredLocations: null,
    filteredCategory: null,
    filteredSentiment: null,
    filteredSources: null,
    filteredCompanies: null,
    filteredKeywords: null
  }),
  mounted() {
    this.initializeData()
  },
  beforeDestroy() {
    // this is for switching from all companies to single company
    if (
      this.activeCompany._id !== 'ALL_COMPANIES' &&
      this.$route.name === 'insights-detailsv2' &&
      !this.$route.query.preventRedirect // prevents going to summary page on subcategory chart click
    ) {
      this.$router.push('/insights/v2/summary')
    }
  },
  computed: {
    ...LocationModule.mapGetters([
      'selectActiveLocations',
      'selectDisabledLocations',
      'selectLocation',
      'selectInsightsLocations'
    ]),
    ...CompanyModule.mapGetters([
      'activeCompany',
      'selectInsightsCompanies',
      'selectOldestCompanyStartDate'
    ]),
    locations() {
      let locations = this.selectActiveLocations.filter((l) => !l.hideFromReports)
      if (this.filteredCompanies?.length) {
        locations = locations.filter((l) =>
          this.filteredCompanies?.includes(l.company || this.selectLocation(l)?.company)
        )
      }
      return locations
    },
    locationRefreshKey() {
      return this.filteredCompanies?.join('') || 0
    }
  },
  methods: {
    ...InsightsDetailsModule.mapActions(['applyDetailFilters']),
    ...CompanyModule.mapActions(['setAllCompanies']),
    async initializeData() {
      this.filteredDateRange = this.$route.query.dateRange
      this.filteredSentiment = this.$route.query.sentiment
      this.filteredCategory = this.$route.query.category
      this.filteredCompanies = this.$route.query.c
      this.filteredLocations = this.$route.query.locs
      this.filteredGroups = this.$route.query.groups
      this.filteredDisabledLocations = this.$route.query.disLocs
      this.filteredSources = this.$route.query.sources
      this.filteredKeywords = this.$route.query.keywords

      if (this.filteredCompanies && !Array.isArray(this.filteredCompanies)) {
        this.filteredCompanies = [this.filteredCompanies]
      }
      if (this.filteredCompanies?.length > 1) {
        await this.setAllCompanies()
      }
      if (this.filteredGroups && !Array.isArray(this.filteredGroups)) {
        this.filteredGroups = [this.filteredGroups]
      }
      if (this.filteredLocations && !Array.isArray(this.filteredLocations)) {
        this.filteredLocations = [this.filteredLocations]
      }
      if (this.filteredDisabledLocations && !Array.isArray(this.filteredDisabledLocations)) {
        this.filteredDisabledLocations = [this.filteredDisabledLocations]
      }
      if (this.filteredCompanies && !Array.isArray(this.filteredCompanies)) {
        this.filteredCompanies = [this.filteredCompanies]
      }
      if (this.filteredSources && !Array.isArray(this.filteredSources)) {
        this.filteredSources = [this.filteredSources]
      }
      if (this.filteredKeywords && !Array.isArray(this.filteredKeywords)) {
        this.filteredKeywords = [this.filteredKeywords]
      }
      if (!(await this.accessPermitted())) {
        this.$router.push('/insights/v2/summary')
        this.$notify({ text: 'You lack permissions needed to view this link', type: 'error' })
      } else {
        this.initialized = true
      }
    },
    companyUpdate(val) {
      this.filteredCompanies = val
      //this.formatAndApplyFilters()
    },
    categoryUpdate(val) {
      this.filteredCategory = val
      //this.formatAndApplyFilters()
    },
    sentimentUpdate(val) {
      this.filteredSentiment = val
      //this.formatAndApplyFilters()
    },
    groupingUpdate(val) {
      this.filteredGroups = val && val.groupIds && val.groupIds.length ? val.groupIds : null
      this.filteredGroupMembers =
        val && val.memberIds && val.memberIds.length ? val.memberIds : null
      this.filteredLocations =
        val && val.locationIds && val.locationIds.length ? val.locationIds : null
      //this.formatAndApplyFilters()
    },
    dateUpdate(val) {
      this.filteredDateRange = val
      if (!this.initialLoadComplete) {
        this.formatAndApplyFilters()
        this.initialLoadComplete = true
      }
    },
    sourceUpdate(val) {
      this.filteredSources = val
      //this.formatAndApplyFilters()
    },
    keywordUpdate(val) {
      this.filteredKeywords = [...val]
      //this.formatAndApplyFilters()
    },
    async formatAndApplyFilters() {
      let locationsToFilter = [...(this.filteredLocations || [])]
      await this.applyDetailFilters({
        companies: this.filteredCompanies?.length ? this.filteredCompanies : null,
        groups: this.filteredGroups?.length ? this.filteredGroups : null,
        locations: locationsToFilter.length ? locationsToFilter : null,
        dateRange:
          this.filteredDateRange && Object.keys(this.filteredDateRange).length
            ? this.filteredDateRange
            : null,
        category: this.filteredCategory,
        sentiment: this.filteredSentiment,
        sources: this.filteredSources,
        keywords: this.filteredKeywords
      })
      this.$emit('filtersApplied')
      this.$router
        .replace({
          path: this.$route.path,
          query: {
            dateRange: this.filteredDateRange,
            category: this.filteredCategory,
            sentiment: this.filteredSentiment,
            ...(this.filteredGroups && this.filteredGroups.length
              ? { groups: this.filteredGroups }
              : {
                  ...(this.filteredLocations?.length ? { locs: this.filteredLocations } : {})
                }),
            ...(this.filteredSources?.length ? { sources: this.filteredSources } : {}),
            ...(this.filteredCompanies?.length ? { c: this.filteredCompanies } : {}),
            ...(this.filteredKeywords?.length ? { keywords: this.filteredKeywords } : {})
          }
        })
        .catch((err) => {
          if (err.name !== 'NavigationDuplicated') throw err
        })
    },
    async accessPermitted() {
      let permitted = true
      if (this.filteredCompanies?.length) {
        permitted = this.filteredCompanies.every((c) =>
          this.selectInsightsCompanies.find((ic) => ic._id === c)
        )
      }
      if (this.filteredLocations?.length) {
        permitted = this.filteredLocations.every((l) =>
          this.selectInsightsLocations.find((il) => il._id === l)
        )
      }
      return permitted
    }
  }
}
</script>
